/**
 * RUTENETTENES CELLER I GLASS 2 — for kalenderen og vakt-/ressursrutenettene (DESIGN.md §16.5
 * punkt 9 og 11). Rutenettene selv bor i hver sin feature-mappe (`MonthGrid`, `TimeGrid`,
 * `ShiftGrid`, `ResourceWeekGrid`); det de skal se like ut i, bor her, så de ikke får fire
 * varianter av den samme plussen.
 */
import type { ComponentProps, CSSProperties, ReactNode } from 'react';

import { cn } from '@/lib/cn';

/**
 * En kalenderhendelse tonet i kategorifargen: 14 % fyll (§16.5 punkt 11). `colour` er en
 * `var(--color-cat-N)` eller en annen token — aldri en hex.
 */
export function categoryFill(colour: string): CSSProperties {
  return { backgroundColor: `color-mix(in srgb, ${colour} 14%, transparent)` };
}

/** Prikken foran hendelsens navn, i den samme fargen som fyllet. */
export function CategoryDot({
  colour,
  className,
}: {
  colour: string;
  className?: string;
}): ReactNode {
  return (
    <span
      aria-hidden
      className={cn('inline-block size-1.5 shrink-0 rounded-full', className)}
      style={{ backgroundColor: colour }}
    />
  );
}

/**
 * EN TOM CELLE: ingen stiplet boks, bare en svak «+» som blir tydelig ved hover og fokus (§16.5
 * punkt 9). Knappen fyller cella, så hele cella er fortsatt klikkbar. Gi den `aria-label` («Legg
 * til vakt på Sekretæriat, fredag») — plusset alene sier ingenting til en skjermleser.
 */
export function GridAddButton({
  className,
  children,
  ...rest
}: ComponentProps<'button'>): ReactNode {
  return (
    <button
      type="button"
      {...rest}
      data-unstyled
      className={cn(
        'group/add hit-target flex min-h-7 w-full items-center justify-center gap-1',
        'rounded-[var(--radius-sm)] text-sm text-faint ring-focus',
        'transition-colors duration-[var(--duration-fast)]',
        'hover:bg-[var(--fill-tonal)] hover:text-accent-text focus-visible:text-accent-text',
        className,
      )}
    >
      <span
        aria-hidden
        className="opacity-50 transition-opacity duration-[var(--duration-fast)] group-hover:opacity-100 group-hover/add:opacity-100 group-focus-visible/add:opacity-100"
      >
        +
      </span>
      {children}
    </button>
  );
}

/**
 * Avkrysningen i en vaktcelle: vises når pekeren er over cella (`group`), når den har fokus, eller
 * når en markering er i gang (`data-selecting="true"` på rutenettet). Ellers tar den ingen plass i
 * blikket — men den er der for tastaturet hele tiden.
 */
export const REVEAL_ON_CELL_HOVER = cn(
  'opacity-0 transition-opacity duration-[var(--duration-fast)]',
  'group-hover:opacity-100 focus-visible:opacity-100 checked:opacity-100',
  '[[data-selecting=true]_&]:opacity-100',
);
